import { formatTime, getUserName } from '../Utils/GeneralUtils';
import { businessToday, getPreferredGymId } from './TaskUtils';

/**
 * One clock for every kind of saved progress: guided flow runs, in-task SOP
 * ticks, daily-report drafts.
 *
 * Progress dies 150 minutes after the last activity (`Constants.PROGRESS_TTL_MINUTES`
 * server-side) so a shift change never surfaces the previous trainer's
 * half-done work, and every surface that shows progress prints the same expiry.
 * Trainers share devices, so browser-local progress is keyed to the signed-in
 * user as well as the gym and the business day.
 */

export const PROGRESS_TTL_MINUTES = 150;
export const PROGRESS_TTL_MS = PROGRESS_TTL_MINUTES * 60 * 1000;

const millis = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
};

/** ISO expiry for progress last touched at `lastActivityIso`; '' when unparseable. */
export const expiresAtFrom = (lastActivityIso) => {
    const ms = millis(lastActivityIso);
    return ms === null ? '' : new Date(ms + PROGRESS_TTL_MS).toISOString();
};

/** Undated progress counts as expired: there is no way to say it is still live. */
export const isExpired = (expiresAtIso, now = Date.now()) => {
    const ms = millis(expiresAtIso);
    if (ms === null) return true;
    return ms <= (now instanceof Date ? now.getTime() : Number(now));
};

/** 'expires 10:42pm', or '' when there is no usable timestamp. */
export const formatExpiry = (expiresAtIso) => {
    const at = formatTime(expiresAtIso);
    return at ? `expires ${at}` : '';
};

/** The signed-in trainer, or '' when nobody is signed in. */
export const progressUser = () => {
    try {
        return getUserName() || '';
    } catch (e) {
        return '';
    }
};

// Colons separate key segments, so nothing but the caller's tail may carry one.
const segment = (value) => encodeURIComponent(String(value ?? ''));

/**
 * `<scope>:<user>:<gymId>:<businessDate>` plus `extra`, which carries whatever
 * else scopes one entry (a task id, say). The overrides exist for callers that
 * already know the gym and day they are writing for.
 */
export const progressKey = (scope, extra = [], overrides = {}) => {
    const {
        user = progressUser(),
        gymId = getPreferredGymId(),
        businessDate = businessToday(),
    } = overrides;
    const tail = (Array.isArray(extra) ? extra : [extra])
        .filter((value) => value !== undefined && value !== null && value !== '')
        .map(String);
    return [String(scope || ''), segment(user), segment(gymId), segment(businessDate), ...tail].join(':');
};
